<template>
    <div>
        <h1 class="page-title">VERSIONS MOBILES APPROUVÉES</h1>
    </div>
            
    <Toast />
    <ConfirmDialog group="global" :closeOnEscape="false" :closable="false" />
    <div class="button-container">
        <div class="dropdown-button">
            <Button label="Ajouter" icon="pi pi-plus" @click="isDialogVisible=true" class="custom-button" />
        </div>
    </div>
    <div class="card">
        <DataTable
            lazy
            removableSort
            paginator
            filterDisplay="row"
            tableStyle="min-width: 50rem"
            showGridlines
            stripedRows
            columnResizeMode="fit"
            :value="tableData"
            :rowHover="true"
            :rows="rows"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :loading="isLoading"
            :rowsPerPageOptions="rowsPerPageOptions"
            @page="onPaginate"
            @sort="onSort($event)"
        >
            <template #header>
                <div class="search-container">
                    <span class="p-input-icon-left">
                        <i class="pi pi-search" />
                        <InputText
                            v-model="globalSearch"
                            placeholder="Rechercher par version, platforme"
                            @keyup.enter="onGlobalFilterKeyEnter()"
                            @input="onGlobalFilterInput()"
                        />
                    </span>
                </div>
            </template>
            <Column field="version" header="Version" :showFilterMenu="false" sortable>
                <template #filter>
                    <InputText
                        v-model="filters.version.fieldValue"
                        @keyup.enter="onColumnSearch()"
                        placeholder="Rechercher"
                        class="p-column-filter"
                        style="min-width: 12rem"
                    />
                </template>
            </Column>
            <Column field='os' header="Platforme" :showFilterMenu="false" sortable>
                <template #filter>
                    <Dropdown v-model="filters.os.fieldValue"
                        :options="[
                            { label: 'iOS', value: 'iOS', icon: 'pi pi-apple', severity: 'info' },
                            { label: 'ANDROID', value: 'ANDROID', icon: 'pi pi-android', severity: 'success' }
                        ]"
                        @change="onColumnSearch()"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Selectionnez"
                        class="p-column-filter"
                        style="min-width: 12rem"
                        :showClear="true"
                    >
                        <template #option="slotProps">
                            <Tag :value="slotProps.option.label" :severity="slotProps.option.severity" :icon="slotProps.option.icon" />
                        </template>
                        <template #value="slotProps">
                            <template v-if="slotProps.value !== null">
                                <Tag
                                    :value="slotProps.value === 'iOS' ? 'iOS' : 'ANDROID'"
                                    :severity="slotProps.value === 'iOS' ? 'info' : 'success'"
                                    :icon="slotProps.value === 'iOS' ? 'pi pi-apple' : 'pi pi-android'"
                                />
                            </template>
                            <template v-else>
                                Selectionnez
                            </template>
                        </template>
                    </Dropdown>
                </template>
                <template #body="slotProps">
                    <Tag
                        :value="slotProps.data.os"
                        :severity="slotProps.data.os === 'iOS' ? 'info' : 'success'"
                        :icon="slotProps.data.os === 'iOS' ? 'pi pi-apple' : 'pi pi-android'"
                    />
                </template>
            </Column>
            <Column field='domain' header="Domaine" :showFilterMenu="false" sortable>
                <template #filter>
                    <Dropdown v-model="filters.domain.fieldValue"
                        :options="domainOptions"
                              @change="onColumnSearch()"
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Selectionnez"
                              class="p-column-filter"
                              style="min-width: 12rem"
                              :showClear="true"
                    >
                        <template #option="slotProps">
                            <Tag :value="slotProps.option.label" :severity="slotProps.option.severity" />
                        </template>
                        <template #value="slotProps">
                            <template v-if="slotProps.value !== null">
                                <Tag :value="slotProps.value" :severity="getSeverity(slotProps.value)" />
                            </template>
                            <template v-else>Selectionnez</template>
                        </template>
                    </Dropdown>
                </template>
                <template #body="slotProps">
                    <Tag :value="slotProps.data.domain" :severity="getSeverity(slotProps.data.domain)" :data-domain="slotProps.data.domain"/>
                </template>
            </Column>
            <Column field="createdAt" header="Date d'ajout" sortable>
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.createdAt) }}
                </template>
            </Column>
            <Column header="Ajouté par">
                <template #body="slotProps">
                    {{ slotProps.data.createdBy ? `${slotProps.data.createdBy.firstname} ${slotProps.data.createdBy.lastname}` : '' }}
                </template>
            </Column>
            <Column field="approved" header="Statut" :showFilterMenu="false" sortable>
                <template #filter>
                    <Dropdown v-model="filters.approved.fieldValue"
                        :options="[
                            { label: 'Actif', value: 1, severity: 'success' },
                            { label: 'Inactif', value: 0, severity: 'danger' }
                        ]"
                        @change="onColumnSearch"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Selectionnez"
                        class="p-column-filter"
                        style="min-width: 12rem"
                        :showClear="true"
                    >
                        <template #option="slotProps">
                            <Tag :value="slotProps.option.label" :severity="slotProps.option.severity"/>
                        </template>
                        <template #value="slotProps">
                            <template v-if="slotProps.value !== null">
                                <Tag :value="slotProps.value === 1 ? 'Actif' : 'Inactif'"
                                    :severity="slotProps.value === 1 ? 'success' : 'danger'"
                                />
                            </template>
                            <template v-else>
                                Selectionnez
                            </template>
                        </template>
                    </Dropdown>
                </template>
                <template #body="slotProps">
                    <Tag :value="slotProps.data.approved ? 'Actif' : 'Inactif'" :severity="slotProps.data.approved ? 'success' : 'danger'" rounded />
                </template>
            </Column>
            <Column>
                <template #header>
                    <div>Actions</div>
                </template>
                <template #body="slotProps">
                    <InputSwitch :model-value="slotProps.data.approved" @update:modelValue="(value) => confirmChangeStatusDialog(value, slotProps.data)" />
                    <Button @click="() => confirmDeleteDialog(slotProps.data)" type="button" title="Supprimer" icon="pi pi-trash" class="ml-1 p-button-danger"></Button>
                </template>
            </Column>
            <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
    </div>
    
    <AddApprovedVersionModal v-model:visible="isDialogVisible" @update:form-submitted="handleFormSubmit($event)"/>
</template>

<script lang="ts" setup>

import {ref, reactive, onMounted} from 'vue';
import AddApprovedVersionModal from '@/components/modal/approvedVersion/AddApprovedVersionModal.vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import InputSwitch from "primevue/inputswitch";
import Column from "primevue/column";
import {ApprovedVersionService} from "@/_services/securite/approvedVersion/ApprovedVersionService";
import {ApprovedVersion} from "@/models/securite/approvedVersion/ApprovedVersion";
import {IApprovedVersionFormSubmittedEvent} from "@/models/ApprovedVersion/IApprovedVersion";

const toast = useToast();
const isDialogVisible = ref(false);
const isLoading = ref<boolean>(true);
const confirm = useConfirm();
const tableData = ref<ApprovedVersion[]>([]);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const currentPage = ref<number>(0);
const globalSearch = ref<string>("");
const modeSortable = ref<boolean>(false);
const defaultsSort = {
    fieldLabel: "order[createdAt]",
    fieldValue: "desc",
};
const formResult = ref<IApprovedVersionFormSubmittedEvent | null>(null);
const filters = reactive({
    version: {
        fieldLabel: "version",
        fieldValue: "",
    },
    os: {
        fieldLabel: "os",
        fieldValue: null,
    },
    approved: {
        fieldLabel: "approved",
        fieldValue: null as number | null,
    },
    domain: {
        fieldLabel: "domain",
        fieldValue: null,
    },
});
const activeFilters = reactive<Record<string, string | number>>({});
const domainOptions = [
    { label: 'gifteo', value: 'gifteo', severity: 'danger' },
    { label: 'club-employes', value: 'club-employes', severity: 'info' },
];

onMounted(async () => {
    await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
});

const getSeverity = (value: string) => {
    const option = domainOptions.find(opt => opt.value === value);
    return option ? option.severity : null;
};

const populateDatatable = async (
    itemsPerPage: number,
    page: number,
    globalSearch?:string,
    sort?: { fieldLabel: string; fieldValue: string; },
    filters?: Record<string, string | number>,
) => {
    const [isLoadingDt, totalRecordsDt, tableDataDt] = await ApprovedVersionService.fetchApprovedVersions(itemsPerPage, page, globalSearch, sort, filters);
    isLoading.value = isLoadingDt;
    totalRecords.value = totalRecordsDt;
    tableData.value = tableDataDt;
    hideSpinner();
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    showSpinner();
    await populateDatatable(e.rows, e.page + 1, globalSearch.value, defaultsSort);
};

const onColumnSearch = async () => {
    Object.keys(activeFilters).forEach(key => delete activeFilters[key]);
    Object.entries(filters).forEach(([key, filter]) => {
        if (typeof filter.fieldValue === 'string' && filter.fieldValue !== '') {
            activeFilters[filter.fieldLabel] = filter.fieldValue.trim();
        }

        if (key === 'approved' && filter.fieldValue !== null) {
            activeFilters[filter.fieldLabel] = filter.fieldValue;
        }
    });

    showSpinner();
    await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort, activeFilters);
};

const onGlobalFilterKeyEnter = () => {
    if (globalSearch.value.trim() != "") {
        showSpinner();
        clearActiveFilters();
        populateDatatable(rows.value, page.value, globalSearch.value);
    }
};

const onGlobalFilterInput = () => {
    if (globalSearch.value === "") {
        showSpinner();
        clearActiveFilters();
        populateDatatable(rows.value, page.value);
    }
};

const clearActiveFilters = () => {
    const nullFilterKeys = ['approved', 'os', 'domain'];
    Object.entries(filters).forEach(([key, filter]) => {
        filter.fieldValue = nullFilterKeys.includes(key) ? null : '';
    });
    Object.keys(activeFilters).forEach(key => delete activeFilters[key]); 
}

const clearGlobalFilter = () => {
    globalSearch.value = "";
}

const onSort = (e) => {
    showSpinner();
    if (e.sortField) {
        const sort = {
            fieldLabel: "order[" + e.sortField + "]",
            fieldValue: e.sortOrder === 1 ? "asc" : "desc",
        };
        modeSortable.value = true;
        populateDatatable(rows.value, page.value, globalSearch.value, sort, activeFilters);
    } else {
        modeSortable.value = false;
        populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort, activeFilters);
    }
};

const handleFormSubmit = async (result: IApprovedVersionFormSubmittedEvent) => {
    formResult.value = result;
    if (formResult.value.status == "success") {
        toast.add({ severity: 'success', summary: 'Success', detail: formResult.value.message, life: 3000 });
        clearActiveFilters();
        clearGlobalFilter();
        await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
    } else {
        toast.add({ severity: 'error', summary: 'Error', detail: formResult.value.message, life: 6000 });
    }
    isDialogVisible.value = false;
    formResult.value = null;
};

const confirmChangeStatusDialog = async (newValue: boolean, data: ApprovedVersion) => {
    const currentStatus = data.approved;
    let newStatus = currentStatus ? 'désactiver' : 'activer';
    confirm.require({
        group: 'global',
        message: `Êtes-vous sûr de vouloir ${newStatus} cette version ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        acceptClass: 'my-custom-accept-class',
        accept: async () => {
            try {
                showSpinner();
                await ApprovedVersionService.toggleApprovedStatus(data).then((response) => {
                    if (!response) {
                        throw Error
                    }
                });
                toast.add({ severity: 'success', summary: 'Success', detail: 'Le statut a bien été modifié !', life: 3000 });
                clearActiveFilters();
                await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
            }  catch (error) {
                toast.add({ severity: 'error', summary: 'Error', detail: 'Une erreur est survenue.', life: 3000 });
                hideSpinner();
            }
        },
    });
};

const confirmDeleteDialog = (data: ApprovedVersion) => {
    confirm.require({
        group: 'global',
        message: `Êtes-vous sûr de vouloir supprimer cette version ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        acceptClass: 'my-custom-accept-class',
        accept: async () => {
            try {
                showSpinner();
                clearActiveFilters();
                await ApprovedVersionService.deleteApprovedVersion(data).then((response) => {
                    if (!response) {
                        throw Error
                    }
                });
                toast.add({ severity: 'success', summary: 'Supprimé', detail: 'Version supprimée avec succès', life: 3000 });
                await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la suppression', life: 3000 });
                hideSpinner();
            }
        }
    });
};

const formatDate = (dateString: string) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const showSpinner = () => {
    isLoading.value = true;
};

const hideSpinner = () => {
    isLoading.value = false;
};
</script>

<style scoped>
.button-container {
    margin-bottom: 1em;
    text-align: right;
}

.custom-button {
    background-color: #007bff;
    color: #ffffff;
    border: none;
}

.custom-button:hover {
    background-color: #0056b3;
}

.dropdown-button {
    display: inline-block;
    position: relative;
}
</style>
